/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import AutocompleteFilterCell from './AutocompleteFilterCell';
import DateTimeFilterCell from './DateTimeFilterCell';
import { Button } from '@totalsoft/rocket-ui';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { usePrevious } from 'customHooks/usePrevious';
import {
  Grid as BrowserGrid,
  VirtualTable,
  TableHeaderRow,
  TableFilterRow,
  PagingPanel,
  TableSelection
} from '@devexpress/dx-react-grid-material-ui';
import {
  SortingState,
  IntegratedSorting,
  FilteringState,
  PagingState,
  CustomPaging,
  SelectionState,
  IntegratedSelection,
  TableColumnVisibility
} from '@devexpress/dx-react-grid';
import WrappedFakeText from '../WrappedFakeText';
import { BoxActionsWrapper } from '../CommonStyles';

const defaultPager = {
  page: 0,
  pageSize: 5
};

const getFilterValue = (filters, columnName) => {
  const filter = filters.find(f => f.columnName === columnName);
  return filter ? filter.value : undefined;
};

const createPostBody = (filters, filterColumns, pager) => {
  let filterProperties = {};
  filterColumns.forEach(x => {
    filterProperties = {
      ...filterProperties,
      [x.columnName]: getFilterValue(filters, x.columnName) ? getFilterValue(filters, x.columnName) : x.columnValue ? x.columnValue : null
    };
  });
  return {
    page: pager.page + 1,
    pageSize: pager.pageSize,
    ...filterProperties
  };
};

const FilterCell = props => {
  const { column } = props;
  if (column.isAutocomplete === 'true') {
    const newProps = { id: column.id, value: column.value, autocompleteOptions: column.autocompleteOptions, ...props };
    return <AutocompleteFilterCell {...newProps} />;
  }
  if (column.isDateTime === 'true') {
    const newProps = { id: column.id, value: column.value, ...props };
    return <DateTimeFilterCell {...newProps} />;
  }
  return <TableFilterRow.Cell {...props} />;
};

const DEBOUNCE_DELAY = 750;

export const BrowserComponent = ({
  idColumnName,
  valueColumnName,
  loadOptions,
  optionsList,
  optionsLoading,
  setSingleSelectedData,
  columns,
  sortingColumns,
  onClose,
  filterColumns,
  hiddenColumnNames,
  onChange,
  t,
  columnExtensions,
  minimumSearchLength,
  hasSimpleValue
}) => {
  const [options, setOptions] = useState(optionsList);
  const [filters, setFilters] = useState([]);
  const [pager, setPager] = useState(defaultPager);
  const [selection, setSelection] = useState([]);
  const [localColumns, setLocalColumns] = useState(
    columns.map(c => ({
      ...c,
      autocompleteOptions: c.loadOptions ? [] : c.autocompleteOptions
    }))
  );

  const prevPage = usePrevious(pager.page);
  const prevFilters = usePrevious(filters);

  const filtersDebounced = debounce(setFilters, DEBOUNCE_DELAY);

  const filterValueChanged = value => {
    filtersDebounced(value);
  };

  const shouldRefresh = useCallback(
    (filters, prevFilters) => {
      let shouldCallApi = false;

      if (filters.length > 0) {
        filters
          .filter(filter => typeof filter.value !== 'string')
          .forEach(filter => {
            const prevFilter = prevFilters.find(pf => pf.columnName === filter.columnName);
            if (!prevFilter || filter.value !== prevFilter.value) shouldCallApi = true;
          });

        if (!shouldCallApi) {
          shouldCallApi = filters
            .filter(filter => typeof filter.value === 'string')
            .reduce((acc, cr) => acc && cr.value.length >= minimumSearchLength, true);
        }
      } else shouldCallApi = true;

      return shouldCallApi;
    },
    [minimumSearchLength]
  );

  const loadOptionsInternal = useCallback(async () => {
    if (!shouldRefresh(filters, prevFilters)) return;
    const requestBody = createPostBody(filters, filterColumns, pager);

    const data = await loadOptions(requestBody);
    setOptions(data);

    if (pager.page === prevPage) {
      setPager({
        ...pager,
        page: 0
      });
    }
  }, [filters, filterColumns, loadOptions, pager, prevFilters, prevPage, shouldRefresh]);

  const changeSelection = item => {
    const lastSelected = item.find(selected => selection.indexOf(selected) === -1);
    if (lastSelected !== undefined) {
      setSelection([lastSelected]);
    } else {
      setSelection([]);
    }
  };

  useEffect(() => {
    const doCallsAsync = async inputColumns => {
      let result = inputColumns;

      for (let index = 0; index < inputColumns.length; index++) {
        const column = inputColumns[index];
        if (!column.loadOptions) continue;
        const data = await column.loadOptions();
        const localColumn = { ...column, autocompleteOptions: [...data] };
        result = [...result.filter(x => x.name !== column.name), localColumn];
      }

      return result;
    };

    doCallsAsync(localColumns).then(newList => {
      setLocalColumns(newList);
    });
  }, []);

  useEffect(() => {
    if (loadOptions) {
      loadOptionsInternal();
    } else {
      if (filters) {
        let filteredOptions = optionsList.values;
        filterColumns.forEach(x => {
          let filter = filters.find(y => y.columnName === x.columnName);
          filteredOptions = filter ? filteredOptions.filter(y => y[x.columnName].includes(filter.value)) : filteredOptions;
          setOptions({ values: filteredOptions, totalCount: filteredOptions.length });
        });
      }
    }
  }, [filters, pager.pageSize, pager.page]);

  const onChangePage = page => {
    setPager(previousPager => ({
      ...previousPager,
      page
    }));
  };

  const onChangePageSize = pageSize => {
    if (pageSize === pager.pageSize) return;

    setPager(previousPager => ({
      ...previousPager,
      pageSize,
      page: 0
    }));
  };

  const handleSaveData = () => {
    let selectedItem = options.values.find(x => x[idColumnName] === selection[0]);
    if (!selectedItem) return;

    let singleSelectedData;

    if (hasSimpleValue) singleSelectedData = selectedItem[valueColumnName];
    else singleSelectedData = { id: selection[0], value: selectedItem[valueColumnName] };

    setSingleSelectedData(singleSelectedData);
    onChange(singleSelectedData);

    return onClose();
  };

  const handleClose = () => {
    return onClose();
  };

  if ((!options || !options.values) && optionsLoading) return <WrappedFakeText lines={7} />;

  if (!options || !options.values) return null;

  return (
    <>
      <BrowserGrid rows={options.values} getRowId={row => row[idColumnName]} columns={localColumns}>
        <FilteringState onFiltersChange={filterValueChanged} />
        <SortingState defaultSorting={sortingColumns} />
        <PagingState
          currentPage={pager.page}
          onCurrentPageChange={onChangePage}
          pageSize={pager.pageSize}
          onPageSizeChange={onChangePageSize}
        />
        <SelectionState selection={selection} onSelectionChange={changeSelection} />
        <IntegratedSorting />
        <CustomPaging totalCount={options.totalCount} />
        <IntegratedSelection />
        <VirtualTable height={400} columnExtensions={columnExtensions} />{' '}
        {/* This renders a scrollable table with fixed height. To remove scroll it can be replaced with <Table> component */}
        <TableSelection selectByRowClick />
        <TableHeaderRow showSortingControls />
        <TableColumnVisibility hiddenColumnNames={hiddenColumnNames} />
        <TableFilterRow cellComponent={FilterCell} />
        <PagingPanel pageSizes={[5, 10, 25, 50, 100]} />
      </BrowserGrid>
      <BoxActionsWrapper>
        <Button onClick={handleSaveData}>{t('Save')}</Button>
        <Button variant='outlined' onClick={handleClose}>
          {t('Close')}
        </Button>
      </BoxActionsWrapper>
    </>
  );
};

BrowserComponent.defaultProps = {
  minimumSearchLength: 1
};

BrowserComponent.propTypes = {
  filterColumns: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  optionsList: PropTypes.object,
  optionsLoading: PropTypes.bool,
  columns: PropTypes.array,
  sortingColumns: PropTypes.array,
  hiddenColumnNames: PropTypes.array,
  loadOptions: PropTypes.func,
  idColumnName: PropTypes.string.isRequired,
  valueColumnName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  columnExtensions: PropTypes.array,
  minimumSearchLength: PropTypes.number,
  hasSimpleValue: PropTypes.bool
};

export default withTranslation()(BrowserComponent);
